import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  AppBar,
  Button,
  Container,
  Stack,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery,
  alpha,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import ContactPhoneRoundedIcon from '@mui/icons-material/ContactPhoneRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import LocalHospital from '@mui/icons-material/LocalHospital';
import MedicalServices from '@mui/icons-material/MedicalServices';
import { useBookMyOpAuth } from '../../contexts/BookMyOpAuthContext';
import { GiHospital } from "react-icons/gi";
import { GiStethoscope } from "react-icons/gi";
import { SiRescuetime } from "react-icons/si";
import { LiaClinicMedicalSolid } from "react-icons/lia";
import { GoDiscussionOutdated } from "react-icons/go";
import { GiClockwork } from "react-icons/gi";
import { RiHome9Line, RiHome9Fill } from "react-icons/ri";
import { PiStethoscopeThin, PiStethoscopeDuotone } from "react-icons/pi";
import { FaRegHospital, FaHospital } from "react-icons/fa6";
import { PiHospitalLight, PiHospitalFill } from "react-icons/pi";
import { PiBookmarksSimpleLight, PiBookmarksSimpleFill } from "react-icons/pi";
import { BiSupport } from "react-icons/bi";
import { MdSupportAgent } from "react-icons/md";
import OrganizationTypeFilter from '../../components/OrganizationTypeFilter';
import EasyOPSwitchButton from '../../components/EasyOPSwitchButton';
import LocationSelector from '../../components/LocationSelector';

const Navbar = ({ notificationBell }) => {
  const theme = useTheme();
  const { isAuthenticated, user, logout, userLocation, refreshProfile } = useBookMyOpAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });


  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    if (isMobile) {
      navigate('/login');
    } else {
      navigate('/');
    }
  };

  const handleLocationUpdate = (locationData) => {
    // Handle location update callback if needed
    setSnackbar({
      open: true,
      message: 'Location updated successfully!',
      severity: 'success'
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      {/* Desktop Navbar - Hidden on mobile */}
      <Box sx={{ 
        display: { xs: 'none', md: 'block' },
        px: { sm: 2, md: 3, lg: 4 }, 
        pt: 1.5, 
        mt: 2,
        position: 'sticky', 
        top: 0, 
        zIndex: 1100,
        bgcolor: theme.palette.background.default
      }}>
        <AppBar 
          position="relative"
          sx={{ 
            background: isScrolled 
            ? theme.palette.background.paper
            : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
            backdropFilter: 'blur(10px)',
            boxShadow: isScrolled 
              ? '0 2px 12px rgba(0, 0, 0, 0.08)'
              : '0 4px 20px rgba(0, 0, 0, 0.12)',
            borderRadius: '16px', 
            mb: 3,
            border: isScrolled
              ? `1px solid ${alpha(theme.palette.divider, 0.5)}`
              : '1px solid rgba(255, 255, 255, 0.3)',
            color: isScrolled ? 'text.primary' : 'white',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <Container maxWidth="xl">
            {/* Single Row: App Name (Left), Location/Filter (Center), Actions (Right) */}
            <Stack 
              direction="row" 
              justifyContent="space-between" 
              alignItems="center" 
              spacing={2}
              sx={{ 
                position: 'relative',
                zIndex: 1,
                py: 1.25,
                px: 1.5,
              }}
            >
              {/* Left: App Name */}
              <Link to="/" style={{ 
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    background: isScrolled 
                      ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                      : 'none',
                    WebkitBackgroundClip: isScrolled ? 'text' : 'none',
                    WebkitTextFillColor: isScrolled ? 'transparent' : 'white',
                    color: isScrolled ? 'transparent' : 'white',
                    fontWeight: 700,
                    display: 'block',
                    fontSize: '1.25rem',
                    letterSpacing: '0.3px',
                    textShadow: isScrolled ? 'none' : '0 1px 3px rgba(0,0,0,0.12)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      opacity: 0.9,
                    }
                  }}
                >
                  BookMyOP
                </Typography>
              </Link>

              {/* Center: Location and Organization Filter */}
              <Stack 
                direction="row" 
                alignItems="center" 
                spacing={1.5}
                sx={{ 
                  flex: 1,
                  justifyContent: 'center',
                  maxWidth: '600px',
                }}
              >
                {/* Location Selector */}
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 200 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: isScrolled ? theme.palette.text.secondary : 'rgba(255, 255, 255, 0.85)',
                      fontSize: '0.7rem',
                      mb: 0.5,
                      fontWeight: 500,
                      letterSpacing: '0.3px',
                    }}
                  >
                    Location
                  </Typography>
                  <LocationSelector
                    onLocationUpdate={handleLocationUpdate}
                    placeholder="Search location..."
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        height: '36px',
                        backgroundColor: isScrolled 
                          ? alpha(theme.palette.primary.main, 0.08)
                          : 'rgba(255, 255, 255, 0.12)',
                        color: isScrolled ? theme.palette.text.primary : 'white',
                        '& .MuiInputBase-input': {
                          color: isScrolled ? theme.palette.text.primary : 'white',
                          fontSize: '0.875rem',
                        },
                        '& .MuiInputBase-input::placeholder': {
                          color: isScrolled ? theme.palette.text.secondary : 'rgba(255, 255, 255, 0.7)',
                        },
                        '& fieldset': {
                          borderColor: isScrolled
                            ? alpha(theme.palette.primary.main, 0.15)
                            : 'rgba(255, 255, 255, 0.25)',
                        },
                        '&:hover fieldset': {
                          borderColor: isScrolled
                            ? theme.palette.primary.main
                            : 'rgba(255, 255, 255, 0.4)',
                        },
                      }
                    }}
                  />
                </Box>

                {/* Organization Type Filter */}
                <OrganizationTypeFilter isScrolled={isScrolled} variant="desktop" />
              </Stack>

              {/* Right: Easy OP Switch, Notification, and User Menu */}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                {/* Easy OP Switch Button */}
                <EasyOPSwitchButton isScrolled={isScrolled} variant="desktop" />
                
                {isAuthenticated && notificationBell && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    '& .MuiIconButton-root': {
                      color: isScrolled 
                        ? theme.palette.primary.main
                        : 'white',
                      padding: '6px',
                      borderRadius: '10px',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      background: isScrolled 
                        ? alpha(theme.palette.primary.main, 0.08)
                        : 'rgba(255, 255, 255, 0.1)',
                      '&:hover': {
                        background: isScrolled 
                          ? alpha(theme.palette.primary.main, 0.12)
                          : 'rgba(255, 255, 255, 0.2)',
                        transform: 'translateY(-1px)',
                      }
                    },
                    '& .MuiBadge-badge': {
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      minWidth: '18px',
                      height: '18px',
                      padding: '0 4px',
                    }
                  }}>
                    {notificationBell}
                  </Box>
                )}
                
                {isAuthenticated ? (
                  <Button 
                    onClick={handleMenuOpen}
                    sx={{
                      color: isScrolled 
                        ? theme.palette.text.primary
                        : 'white',
                      borderRadius: '10px',
                      fontWeight: 600,
                      px: 1.75,
                      py: 0.75,
                      fontSize: '0.875rem',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      background: isScrolled 
                        ? alpha(theme.palette.primary.main, 0.08)
                        : 'rgba(255, 255, 255, 0.12)',
                      backdropFilter: 'blur(10px)',
                      border: isScrolled
                        ? `1px solid ${alpha(theme.palette.primary.main, 0.15)}`
                        : '1px solid rgba(255, 255, 255, 0.25)',
                      '& .MuiButton-startIcon': {
                        color: isScrolled 
                          ? theme.palette.primary.main
                          : 'white',
                        transition: 'all 0.3s ease',
                        marginRight: '6px',
                        '& svg': {
                          fontSize: '1.1rem',
                        }
                      },
                      '&:hover': {
                        background: isScrolled 
                          ? alpha(theme.palette.primary.main, 0.12)
                          : 'rgba(255, 255, 255, 0.2)',
                        transform: 'translateY(-1px)',
                        boxShadow: isScrolled
                          ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                          : '0 4px 12px rgba(0, 0, 0, 0.1)',
                        '& .MuiButton-startIcon': {
                          transform: 'scale(1.05)',
                        }
                      }
                    }}
                    startIcon={<AccountCircleRoundedIcon />}
                  >
                    Hi, {user?.first_name || user?.name || 'User'}
                  </Button>
                ) : (
                  <Button 
                    component={Link}
                    to="/login"
                    variant="contained"
                    sx={{
                      background: isScrolled 
                        ? theme.palette.primary.main
                        : 'rgba(255, 255, 255, 0.2)',
                      color: isScrolled 
                        ? 'white'
                        : 'white',
                      boxShadow: isScrolled 
                        ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                        : '0 2px 8px rgba(0, 0, 0, 0.1)',
                      borderRadius: '10px',
                      fontWeight: 600,
                      px: 2.25,
                      py: 0.75,
                      fontSize: '0.875rem',
                      border: isScrolled
                        ? 'none'
                        : '1px solid rgba(255, 255, 255, 0.25)',
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        background: isScrolled 
                          ? theme.palette.primary.dark
                          : 'rgba(255, 255, 255, 0.3)',
                        transform: 'translateY(-1px)',
                        boxShadow: isScrolled 
                          ? `0 4px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                          : '0 4px 16px rgba(0, 0, 0, 0.15)',
                      }
                    }}
                  >
                    Login
                  </Button>
                )}
              </Stack>
            </Stack>

            {/* User Menu */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  mt: 1.5,
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
                  background: `linear-gradient(135deg, ${theme.palette.primary.light}95, ${theme.palette.secondary.light}95)`,
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  borderRadius: '16px',
                  minWidth: '200px',
                  '&::before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 20,
                    width: 10,
                    height: 10,
                    background: 'inherit',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                    border: '1px solid rgba(255, 255, 255, 0.4)',
                    borderBottom: 'none',
                    borderRight: 'none',
                  },
                }
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem 
                component={Link} 
                to="/appointments" 
                onClick={handleMenuClose}
                sx={{
                  borderRadius: '12px',
                  m: 0.5,
                  p: 1.5,
                  gap: 1.5,
                  color: 'white',
                  transition: 'all 0.3s ease',
                  '& svg': { color: 'white' },
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.2)',
                    '& svg': {
                      transform: 'scale(1.2)',
                    }
                  }
                }}
              >
                {location.pathname === '/appointments' ? <PiBookmarksSimpleFill /> : <PiBookmarksSimpleLight />}
                Appointments
              </MenuItem>
              <MenuItem 
                component={Link} 
                to="/support"
                onClick={handleMenuClose}
                sx={{
                  borderRadius: '12px',
                  m: 0.5,
                  p: 1.5,
                  gap: 1.5,
                  color: 'white',
                  transition: 'all 0.3s ease',
                  '& svg': { color: 'white' },
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.2)',
                    '& svg': {
                      transform: 'scale(1.2)',
                    }
                  }
                }}
              >
                {location.pathname === '/communication' ? <MdSupportAgent /> : <BiSupport />}
                Support
              </MenuItem>
              <MenuItem 
                onClick={handleMenuClose} 
                component={Link} 
                to="/profile"
                sx={{ 
                  borderRadius: '12px',
                  m: 0.5,
                  p: 1.5,
                  gap: 1.5,
                  color: 'white',
                  transition: 'all 0.3s ease',
                  '& svg': { color: 'white' },
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.2)',
                    '& svg': {
                      transform: 'scale(1.2)',
                    }
                  }
                }}
              >
                <AccountCircleRoundedIcon />
                Profile
              </MenuItem>
              <MenuItem 
                onClick={handleMenuClose} 
                component={Link} 
                to="/settings"
                sx={{ 
                  borderRadius: '12px',
                  m: 0.5,
                  p: 1.5,
                  gap: 1.5,
                  color: 'white',
                  transition: 'all 0.3s ease',
                  '& svg': { color: 'white' },
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.2)',
                    '& svg': {
                      transform: 'scale(1.2)',
                    }
                  }
                }}
              >
                <SettingsRoundedIcon />
                Settings
              </MenuItem>
              <Divider sx={{ my: 0.5, borderColor: 'rgba(255, 255, 255, 0.2)' }} />
              <MenuItem 
                onClick={handleLogout} 
                sx={{ 
                  color: 'white',
                  borderRadius: '12px',
                  m: 0.5,
                  p: 1.5,
                  gap: 1.5,
                  transition: 'all 0.3s ease',
                  '& svg': { color: 'white' },
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: '#FF4B4B',
                    '& svg': {
                      color: '#FF4B4B',
                      transform: 'scale(1.2) rotate(5deg)',
                    }
                  }
                }}
              >
                <LogoutRoundedIcon />
                Logout
              </MenuItem>
            </Menu>
          </Container>
        </AppBar>
      </Box>


      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      

    </>
  );
};

export default Navbar; 